import type { SortDirection } from '@/components/ui/data-table';
import type { FamilySummaryViewModel } from '@/models/family';

/**
 * Columns the Browse Families table can sort by. Closed union so the URL param
 * (`?sort=`), header wiring, and comparator can't drift (PTV2-1232 #6).
 */
export type FamilySortKey = 'name' | 'members' | 'lastModified';

const SORT_KEYS: readonly FamilySortKey[] = ['name', 'members', 'lastModified'];

export function parseFamilySortKey(value: string | null): FamilySortKey | null {
  return SORT_KEYS.includes(value as FamilySortKey) ? (value as FamilySortKey) : null;
}

export function parseSortDirection(value: string | null): SortDirection | null {
  return value === 'asc' || value === 'desc' ? value : null;
}

/**
 * Returns a new array sorted by `key`/`direction`. `null` key preserves the
 * original order (list still copied so callers own the result). `members` is a
 * numeric sort; `name` is locale-aware text; `lastModified` is lexical ISO with
 * empties last. Pure — never mutates the input.
 */
export function sortFamilySummaries(
  families: readonly FamilySummaryViewModel[],
  key: FamilySortKey | null,
  direction: SortDirection,
): readonly FamilySummaryViewModel[] {
  if (key === null) return families.slice();
  const factor = direction === 'asc' ? 1 : -1;
  return families.slice().sort((a, b) => {
    if (key === 'members') {
      return (a.memberCount - b.memberCount) * factor;
    }
    const av = key === 'name' ? a.name : a.updatedAt;
    const bv = key === 'name' ? b.name : b.updatedAt;
    // Empties last, irrespective of direction.
    if (av === null || av.length === 0) {
      return bv === null || bv.length === 0 ? 0 : 1;
    }
    if (bv === null || bv.length === 0) return -1;
    if (key === 'lastModified') {
      return av < bv ? -factor : av > bv ? factor : 0;
    }
    return av.localeCompare(bv, undefined, { sensitivity: 'base' }) * factor;
  });
}

/**
 * Cycles a column's sort state: inactive → asc → desc → cleared.
 */
export function nextFamilySort(
  current: { readonly key: FamilySortKey | null; readonly direction: SortDirection },
  clicked: FamilySortKey,
): { readonly key: FamilySortKey | null; readonly direction: SortDirection } {
  if (current.key !== clicked) return { key: clicked, direction: 'asc' };
  if (current.direction === 'asc') return { key: clicked, direction: 'desc' };
  return { key: null, direction: 'asc' };
}
